import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { BarChart3, TrendingUp, TrendingDown, Trophy, Skull, Clock, Flame } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtStx(micro) {
  const v = (micro || 0) / 1_000_000;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtDuration(seconds) {
  if (!seconds || seconds <= 0) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${(seconds / 86400).toFixed(1)}d`;
}

function StatTile({ icon: Icon, label, value, accent, testId, hint }) {
  return (
    <div
      className="rounded-lg border border-border/40 bg-muted/20 p-3"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground mb-1">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`font-mono text-base font-semibold ${accent || ""}`}>
        {value}
      </div>
      {hint && (
        <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
      )}
    </div>
  );
}

/**
 * BotAnalyticsPanel — equity curve + win/loss summary + streaks + side breakdown,
 * powered by GET /api/prediction-bots/:bot_id/analytics (reads from position_history).
 */
export default function BotAnalyticsPanel({ botId, testId = "bot-analytics" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(
          `${API}/api/prediction-bots/${botId}/analytics?max_trades=500`
        );
        const json = await res.json();
        if (active && json?.success) setData(json);
      } catch {
        // silent fail
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [botId]);

  if (loading) {
    return (
      <Card className="glass-card border-border" data-testid={testId}>
        <CardContent className="p-6 text-sm text-muted-foreground text-center">
          Loading analytics…
        </CardContent>
      </Card>
    );
  }
  if (!data || data.total_settled === 0) {
    return (
      <Card className="glass-card border-border" data-testid={testId}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <BarChart3 className="w-4 h-4 text-secondary" />
            Analytics
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground text-center py-4">
          Not enough settled trades yet — analytics will appear once the bot has at least one closed position.
        </CardContent>
      </Card>
    );
  }

  const wl = data.win_loss;
  const sb = data.side_breakdown;
  const st = data.streaks;

  // Convert equity curve points to chart-friendly shape
  const chart = (data.equity_curve || []).map((p, i) => ({
    idx: i,
    ts: p.ts,
    eq: (p.cumulative_pnl_stxoshi || 0) / 1_000_000,
  }));
  const finalEq = chart.length > 0 ? chart[chart.length - 1].eq : 0;
  const eqPositive = finalEq >= 0;

  return (
    <Card className="glass-card border-border" data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="w-4 h-4 text-secondary" />
          Analytics
          <Badge variant="outline" className="text-xs ml-auto">
            {data.total_settled} trades
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Equity curve */}
        <div data-testid="analytics-equity-curve">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-muted-foreground">Equity Curve (cumulative P&L)</span>
            <span
              className={`font-mono text-sm font-semibold ${
                eqPositive ? "text-green-400" : "text-red-400"
              }`}
            >
              {eqPositive ? "+" : ""}
              {finalEq.toLocaleString(undefined, { maximumFractionDigits: 2 })} STXOSHI
            </span>
          </div>
          {data.pnl_breakdown && (
            <div className="mb-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-mono text-muted-foreground" data-testid="analytics-pnl-breakdown">
              <span data-testid="analytics-trading-pnl">
                Trading (last {data.total_settled}): <span className={data.pnl_breakdown.trading_pnl_stxoshi >= 0 ? "text-green-400" : "text-red-400"}>{data.pnl_breakdown.trading_pnl_stxoshi >= 0 ? "+" : ""}{fmtStx(data.pnl_breakdown.trading_pnl_stxoshi)}</span>
              </span>
              <span data-testid="analytics-yield-pnl">
                Yield & earlier trades: <span className={data.pnl_breakdown.yield_pnl_stxoshi >= 0 ? "text-green-400" : "text-red-400"}>{data.pnl_breakdown.yield_pnl_stxoshi >= 0 ? "+" : ""}{fmtStx(data.pnl_breakdown.yield_pnl_stxoshi)}</span>
              </span>
            </div>
          )}
          <div className="h-40 -mx-2">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chart} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <defs>
                  <linearGradient id="eqGradGreen" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4ade80" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#4ade80" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="eqGradRed" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#f87171" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#f87171" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="idx" hide />
                <YAxis hide domain={["auto", "auto"]} />
                <Tooltip
                  contentStyle={{
                    background: "rgba(0,0,0,0.85)",
                    border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: 6,
                    fontSize: 11,
                  }}
                  labelFormatter={() => ""}
                  formatter={(value) => [
                    `${value >= 0 ? "+" : ""}${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })} STXOSHI`,
                    "Cumulative",
                  ]}
                />
                <ReferenceLine y={0} stroke="rgba(255,255,255,0.15)" />
                <Area
                  type="monotone"
                  dataKey="eq"
                  stroke={eqPositive ? "#4ade80" : "#f87171"}
                  strokeWidth={2}
                  fill={`url(#${eqPositive ? "eqGradGreen" : "eqGradRed"})`}
                  isAnimationActive={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Win/loss + averages tiles */}
        <div className="grid grid-cols-2 gap-2" data-testid="analytics-stats-grid">
          <StatTile
            icon={TrendingUp}
            label="Win Rate"
            value={`${wl.win_rate}%`}
            accent={wl.win_rate >= 50 ? "text-green-400" : "text-red-400"}
            hint={`${wl.winning}W / ${wl.losing}L`}
            testId="analytics-win-rate"
          />
          <StatTile
            icon={Clock}
            label="Avg Hold"
            value={fmtDuration(data.avg_hold_seconds)}
            testId="analytics-avg-hold"
          />
          <StatTile
            icon={Trophy}
            label="Best Trade"
            value={`+${wl.best_trade.pnl_pct}%`}
            accent="text-green-400"
            hint={`+${fmtStx(wl.best_trade.pnl_stxoshi)} STXOSHI · ${wl.best_trade.side || "?"}`}
            testId="analytics-best-trade"
          />
          <StatTile
            icon={Skull}
            label="Worst Trade"
            value={`${wl.worst_trade.pnl_pct}%`}
            accent="text-red-400"
            hint={`${fmtStx(wl.worst_trade.pnl_stxoshi)} STXOSHI · ${wl.worst_trade.side || "?"}`}
            testId="analytics-worst-trade"
          />
          <StatTile
            icon={Flame}
            label="Current Streak"
            value={
              st.current?.kind
                ? `${st.current.length} ${st.current.kind}${st.current.length !== 1 ? "s" : ""}`
                : "—"
            }
            accent={
              st.current?.kind === "win"
                ? "text-green-400"
                : st.current?.kind === "loss"
                ? "text-red-400"
                : ""
            }
            hint={`max W:${st.longest_win_streak} L:${st.longest_loss_streak}`}
            testId="analytics-current-streak"
          />
          <StatTile
            icon={TrendingDown}
            label="Avg Win / Loss"
            value={`+${wl.avg_win_pct}% / ${wl.avg_loss_pct}%`}
            testId="analytics-avg-win-loss"
          />
        </div>

        {/* Side breakdown */}
        <div data-testid="analytics-side-breakdown">
          <div className="text-xs text-muted-foreground mb-2">By Side</div>
          <div className="grid grid-cols-2 gap-2">
            {["long", "short"].map((side) => {
              const s = sb?.[side] || { count: 0, pnl_stxoshi: 0, win_rate: 0, avg_pnl_pct: 0 };
              return (
                <div
                  key={side}
                  className={`rounded-lg p-3 border ${
                    side === "long"
                      ? "border-cyan-500/30 bg-cyan-500/5"
                      : "border-purple-500/30 bg-purple-500/5"
                  }`}
                  data-testid={`analytics-side-${side}`}
                >
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-semibold uppercase ${
                        side === "long" ? "text-cyan-300" : "text-purple-300"
                      }`}
                    >
                      {side}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {s.count} trades
                    </span>
                  </div>
                  <div
                    className={`font-mono text-base font-semibold mt-1 ${
                      s.pnl_stxoshi >= 0 ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {s.pnl_stxoshi >= 0 ? "+" : ""}
                    {fmtStx(s.pnl_stxoshi)} STXOSHI
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    {s.win_rate}% win · avg {s.avg_pnl_pct >= 0 ? "+" : ""}
                    {s.avg_pnl_pct}%
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
